// Weather cameras layer family.
//
// Extracted from addOverlays.ts as HI-1 batch 5 (audit 2026-05-22).
// Pure mechanical move — every source, layer, paint expression,
// layout property, and minzoom/maxzoom is byte-identical to its
// pre-extraction form. Source-id idempotency guard preserved.
//
// Uses the LayerFamilySpec shape introduced in batch 9.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC, safeGetSource } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import type { GeoJSONSource } from 'maplibre-gl';

/**
 * Mount the wxcams source + its layers. Idempotent on the source.
 */
export function addWxcamsLayers(map: MlMap): void {
    if (!map.getSource('wxcams')) {
      map.addSource('wxcams', { type: 'geojson', data: EMPTY_FC });
      map.addLayer({
        id: 'wxcams-icon',
        type: 'symbol',
        source: 'wxcams',
        minzoom: 8,
        layout: {
          'icon-image': 'wxcam',
          'icon-size': [
            'interpolate', ['linear'], ['zoom'],
            8,  0.55,
            11, 0.85,
            13, 1.0,
          ],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'visibility': 'none',
        },
      });
    }
}

// Module-level loaded flag (mirrors heliportsLoaded pattern in MapView).
let wxcamsLoaded = false;

/** Fetch the FAA WeatherCams site list and populate the wxcams source.
 *  Moved from MapView to this module as part of HI-1' (2026-05-24).
 *  Single global fetch — the list rarely changes and is small. */
export async function refreshWxCams(map: MlMap): Promise<void> {
  try {
    const r = await fetch('/api/wx/cameras-list');
    if (!r.ok) return;
    const body = (await r.json()) as { sites: Array<{
      id: number; name: string; ident: string; icao: string;
      lat: number; lon: number; elevFt: number; active: boolean;
    }> };
    const sites = (body.sites || []).filter((s: { active: boolean }) => s.active);
    const src = safeGetSource<GeoJSONSource>(map, 'wxcams');
    if (!src) return;
    src.setData({
      type: 'FeatureCollection',
      features: sites.map((s) => ({
        type: 'Feature',
        properties: {
          camId: s.id,
          ident: s.ident || s.icao || `WC${s.id}`,
          name: s.name,
          icao: s.icao,
          elevFt: s.elevFt,
          kind: 'wxcam',
        },
        geometry: { type: 'Point', coordinates: [s.lon, s.lat] },
      })),
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('wxcams fetch', e);
  }
}

registerLayerFamily({
  id: 'wxcams',
  label: 'Weather cameras',
  sourceId: 'wxcams',
  layerIds: ['wxcams-icon'],
  mount: (map) => addWxcamsLayers(map),
  visibilitySelector: (l) => l.wxcams,
  applyState: (map) => {
    if (!map.getLayer('wxcams-icon')) return;
    const show = useLayerVisibilityStore.getState().layers.wxcams;
    map.setLayoutProperty('wxcams-icon', 'visibility', show ? 'visible' : 'none');
  },
  onToggleOn: (map) => {
    if (wxcamsLoaded) return;
    wxcamsLoaded = true;
    return refreshWxCams(map);
  },
});
